<template>
  <div class="cat-tabs" :class="[tabtype, tabClass]">
    <div ref="tabsWrapper" class="cat-tabs-wrapper">
      <button
        v-if="showArrows"
        ref="iconButtonLeft"
        data-button="Icon"
        class="tabs-button tabs-button-left"
        :aria-label="resolveTxt('skin.tabs.left', 'Scroll left')"
        @click="_left"
      ></button>
      <ul
        ref="tabsContainer"
        class="cat-tabs-container"
        role="tablist"
        :aria-label="
          caption ? caption : resolveTxt('skin.tabs.caption', 'Details')
        "
        :aria-orientation="orientation"
      >
        <template v-for="(item, idx) in items" :key="idx">
          <li
            class="cat-tab-wrapper"
            :class="_classes(item)"
            role="presentation"
          >
            <a
              :id="id + '_tab_' + item.step"
              class="tab-focus"
              :class="'cat-tab cat-tab-' + item.step"
              :aria-selected="item.step === selected ? 'true' : 'false'"
              :tabindex="item.step === selected ? '0' : '-1'"
              data-label-type="tab-title"
              :data-label="item.title"
              role="tab"
              :aria-controls="id ? id + '_tab_panel' : null"
              @click="(evt) => _onClick(evt, item.step)"
              @keyup.enter="(evt) => _onClick(evt, item.step)"
              @keydown.space.prevent="(evt) => _onClick(evt, item.step)"
              @keydown.left.prevent="(evt) => previousTab(evt, idx)"
              @keydown.right.prevent="(evt) => nextTab(evt, idx)"
              @keydown.up.prevent="(evt) => previousTab(evt, idx)"
              @keydown.down.prevent="(evt) => nextTab(evt, idx)"
              @keydown.tab="focusScrollOrPanel($event, $el, $refs.tabpanel)"
            >
              <span>{{ item.title }}</span>
              <cat-pill
                v-if="item.pill"
                v-tooltip="item.tooltip"
                :value="item.pillValue"
                :class="badgeType(item.pill)"
                :data-label-type="'badge-' + badgeType(item.pill)"
              />
            </a>
          </li>
        </template>
      </ul>
      <button
        v-if="showArrows"
        ref="iconButtonRight"
        data-button="Icon"
        class="tabs-button tabs-button-right"
        :aria-label="resolveTxt('skin.tabs.right', 'Scroll right')"
        @click="_right"
      ></button>
    </div>
    <div
      :id="id ? id + '_tab_panel' : null"
      ref="tabpanel"
      class="cat-tab-panel"
      :class="tabPanelClass"
      role="tabpanel"
      :aria-label="panelLabel"
      tabindex="0"
      @keydown.tab="focusoutTabPanel($event, $el, $refs.tabpanel)"
      @click="focusoutTabPanel($event, $el, $refs.tabpanel)"
    >
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatPill from "@/components/catPill/cat-pill";
import TabsMixin from "../utilities/tabsMixin.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatTabs",
  components: {
    CatPill
  },
  mixins: [TabsMixin],
  props: {
    id: {
      type: [String, Number],
      description: "Sets the ID attributes of child elements."
    },
    items: {
      type: [Array]
    },
    selected: [String, Number],
    type: {
      type: String,
      default: "tab-links"
    },
    tabClass: String,
    tabPanelClass: String,
    caption: {
      type: String,
      description: "Accessible name to identify the tabs group."
    }
  },
  data: function () {
    return {
      sel: this.selected ? this.selected : 1,
      isActive: false,
      orientation: "horizontal",
      showArrows: false
    };
  },
  mounted() {
    this.isActive = this.selected ? true : false;
    this._setArrows();
    window.addEventListener("resize", this._setArrows);
  },
  watch: {
    selected: function (old, newval) {
      this.sel = newval;
    }
  },
  updated() {
    this._setArrows();
  },

  methods: {
    _setArrows() {
      let t = this;
      this.$nextTick(function () {
        if (
          t.$refs.tabsContainer &&
          t.$refs.tabsContainer.clientWidth <= t.$refs.tabsWrapper.clientWidth
        ) {
          t.showArrows = false;
        } else {
          t.showArrows = true;
        }
      });
    },
    _left: function () {
      if (this.$refs.tabsWrapper) {
        let t = this;
        this.$nextTick(function () {
          t.$refs.tabsWrapper.style.scrollBehavior = "smooth";
          t.$refs.tabsWrapper.scrollLeft -= 180;
        });
      }
    },
    _right: function () {
      if (this.$refs.tabsWrapper) {
        let t = this;
        this.$nextTick(function () {
          t.$refs.tabsWrapper.style.scrollBehavior = "smooth";
          t.$refs.tabsWrapper.scrollLeft += 180;
        });
      }
    },
    _onClick: function (event, step) {
      this.sel = step;
      if (event && event.target && event.target.parentElement) {
        event.target.parentElement.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest"
        });
      }

      this.$emit("ui-click", {
        event: event,
        value: step,
        id: this.id ? this.id : null
      });
    },
    _getRules: function (item) {
      return [() => !(item.errors.length > 0)];
    },
    _classes: function (item, idx) {
      return {
        selected: item.step == this.selected,
        invalid: item.editable && item.invalid,
        warning: item.editable && item.warning,
        infomessage: item.editable && item.infomessage,
        debugmessage: item.debugmessage
      };
    },
    badgeType: function (type) {
      if (type === "true") {
        return "default";
      }
      return type;
    },
    matchingItem: function () {
      this.items[index];
    },

    nextTab: function (event, idx) {
      const index = idx === this.items.length - 1 ? 0 : idx + 1;
      this.focusTab(index);
    },
    previousTab: function (event, idx) {
      const index = idx === 0 ? this.items.length - 1 : idx - 1;
      this.focusTab(index);
    },
    focusTab: function (idx) {
      let index = this.items[idx].step;
      globalThis
        .jQuery(".cat-tab-" + index, this.$el)
        .first()
        .focus();
    },
    focusScrollOrPanel: function (event, el, ref) {
      if (!this.showArrows) {
        this.focusTabPanel(event, el, ref);
      } else if (event.shiftKey) {
        globalThis.jQuery(".tabs-button-left", this.$el).first().focus();
        event.preventDefault();
      } else {
        globalThis.jQuery(".tabs-button-right", this.$el).first().focus();
        event.preventDefault();
      }
    }
  },

  computed: {
    tabtype: function () {
      return {
        "tab-links": this.type === "tab-links",
        "tab-boxed": this.type === "tab-boxed",
        "tab-links tab-secondary": this.type === "tab-secondary",
        "tab-boxed tab-boxed-left": this.type === "tab-boxed-left",
        "one-tab-only": this.items.length === 1
      };
    },
    panelLabel: function () {
      return this.items &&
        this.selected > 0 &&
        this.selected <= this.items.length
        ? this.items[this.selected - 1].title
        : this.resolveTxt("skin.tab.panel.label", "Detail");
    }
  }
});
</script>
